import React, { useState, useMemo, useCallback } from 'react';
import { XIcon } from '@phosphor-icons/react';
import * as Sentry from '@sentry/react';
import useCustomers, {
  CustomerPreviewDTO,
} from 'src/api/accounts-receivable/customers/useCustomers';
import useCustomersByIds, {
  CustomerBasicInfo,
} from 'src/api/accounts-receivable/customers/useCustomersByIds';

import {
  SalesOrderDraftStatus,
  SalesOrderSource,
} from 'src/types/application/SalesOrderDraftDTO';
import { WatchStatus } from '@/types/application/WatcherDTO';
import { Autocomplete } from '@/components/common/inputs/autocomplete';
import { pickerLabels } from '@/components/common/inputs/pickerLabels';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { FloatingLabelWrapper } from '@/components/common/inputs/floating-label-wrapper';
import { T, Var, useGT } from 'gt-react';
import {
  useGetSalesAssignment,
  useListSalesAssignments,
} from 'src/api/rpc/hooks/resource-privileges/useResourcePrivileges';
import { formatDateOnlyString } from 'src/utils/dateUtils';
import { DateRangePresetFilter } from '@/components/common/date-pickers/DateRangePresetFilter';

export interface FilterValues {
  customerIds?: string[];
  excludedCustomerIds?: string[];
  statuses?: SalesOrderDraftStatus[];
  sources?: SalesOrderSource[];
  watchStatus?: WatchStatus[];
  purchaseOrderNumber?: string;
  /** v3 only: orders for the customers this person is assigned (Data Access). */
  salesAssigneeUserId?: string;
  /** v3 only: inclusive `receivedDate` bounds, ISO `YYYY-MM-DD`. */
  receivedFrom?: string;
  receivedTo?: string;
  /** v3 only: only orders submitted to the ERP still waiting for an ERP order #. */
  erpNumberPending?: boolean;
}

interface Props {
  filters: FilterValues;
  onFiltersChange: (filters: FilterValues) => void;
  /**
   * Override the Status filter's options + label. Defaults to the v2 draft
   * statuses (New / Approved / Rejected). The v3 list passes lifecycle-stage
   * options so the filter matches the Stage column it shows.
   */
  statusOptions?: { value: string; label: string }[];
  statusFilterLabel?: string;
  /**
   * Show the Watching/Assigned filter. The v3 list never sends watchStatus to
   * its API, so it hides this. Defaults to true for the legacy v2 page.
   */
  showWatchFilter?: boolean;
  /**
   * v3 only, RBAC-gated by the page: show the "Filter by User" autocomplete
   * (people with a sales assignment) driving `salesAssigneeUserId`.
   */
  showAssigneeFilter?: boolean;
  /**
   * v3 only: show the Received date range filter (calendar + quick presets)
   * driving `receivedFrom` / `receivedTo`. Off for the legacy v2 page.
   */
  showDateFilter?: boolean;
  /**
   * v3 only, shown where the ERP Order # column shows: the "Pending ERP
   * Order #" checkbox driving `erpNumberPending`.
   */
  showErpPendingFilter?: boolean;
}

// Helper function to convert CustomerPreviewDTO to CustomerBasicInfo
const convertToBasicInfo = (
  customer: CustomerPreviewDTO,
): CustomerBasicInfo => ({
  customerId: customer.id,
  name: customer.name,
  erpDisplayName: customer.erpDisplayName ?? null,
});

/** Active-filter chip for "sales orders for this person" (v3 Data Access link). */
const SalesAssigneeBadge: React.FC<{ userId: string; onClear: () => void }> = ({
  userId,
  onClear,
}) => {
  const assignment = useGetSalesAssignment(userId);
  const label = assignment.data?.userLabel ?? '…';
  return (
    <Badge variant="outline" className="cursor-pointer hover:bg-secondary pr-1">
      <T context="active filter badge: orders visible to an assigned person">
        Sales orders for: <Var>{label}</Var>
      </T>
      <button
        onClick={onClear}
        className="ml-1 rounded-full hover:bg-secondary/80 p-0.5"
      >
        <XIcon className="h-3 w-3" />
      </button>
    </Badge>
  );
};

const SalesOrderFilters: React.FC<Props> = ({
  filters,
  onFiltersChange,
  statusOptions: statusOptionsProp,
  statusFilterLabel,
  showWatchFilter = true,
  showAssigneeFilter = false,
  showDateFilter = false,
  showErpPendingFilter = false,
}) => {
  const gt = useGT();

  const getSourceLabel = useCallback(
    (source: SalesOrderSource): string => {
      switch (source) {
        case SalesOrderSource.Edi:
          return gt('EDI', { context: 'sales order source filter label' });
        case SalesOrderSource.Email:
          return gt('Email', { context: 'sales order source filter label' });
        case SalesOrderSource.DirectEntry:
          return gt('Direct Entry', {
            context: 'sales order source filter label',
          });
        case SalesOrderSource.FileUpload:
          return gt('File Upload', {
            context: 'sales order source filter label',
          });
        case SalesOrderSource.ErpSync:
          return gt('ERP Sync', { context: 'sales order source filter label' });
        default: {
          const exhaustiveCheck: never = source;
          const error = new Error(
            `Unhandled SalesOrderSource: ${exhaustiveCheck}`,
          );
          console.error(error);
          Sentry.captureException(error);
          return gt('Unknown', {
            context: 'fallback sales order source label',
          });
        }
      }
    },
    [gt],
  );

  const statusOptions = useMemo(
    () =>
      statusOptionsProp ?? [
        {
          value: SalesOrderDraftStatus.RECEIVED as string,
          label: gt('New', { context: 'sales order draft status filter' }),
        },
        {
          value: SalesOrderDraftStatus.APPROVED as string,
          label: gt('Approved', { context: 'sales order draft status filter' }),
        },
        {
          value: SalesOrderDraftStatus.REJECTED as string,
          label: gt('Rejected', { context: 'sales order draft status filter' }),
        },
        {
          value: SalesOrderDraftStatus.ERP_SYNC_FAILED as string,
          label: gt('ERP Sync Failed', {
            context: 'sales order draft status filter',
          }),
        },
      ],
    [gt, statusOptionsProp],
  );

  const sourceOptions = useMemo(
    () =>
      Object.values(SalesOrderSource).map((source) => ({
        value: source,
        label: getSourceLabel(source),
      })),
    [getSourceLabel],
  );

  const watchStatusOptions = useMemo(
    () => [
      {
        value: 'watching' as WatchStatus,
        label: gt('Watching', { context: 'sales order watch filter' }),
      },
      {
        value: 'assigned' as WatchStatus,
        label: gt('Assigned', { context: 'sales order watch filter' }),
      },
    ],
    [gt],
  );

  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  // "Filter by User": people with a sales assignment (client-filtered — the
  // list is small). Only fetched when the page enabled the filter.
  const salesAssignments = useListSalesAssignments(
    undefined,
    showAssigneeFilter,
  );
  const assigneeOptions = salesAssignments.data?.assignments ?? [];
  const [excludedCustomerSearchQuery, setExcludedCustomerSearchQuery] =
    useState('');

  // Fetch customers for autocomplete
  const { data: customersData, isLoading: customersLoading } = useCustomers({
    pageSize: 10,
    name: customerSearchQuery.length > 2 ? customerSearchQuery : undefined,
  });

  // Fetch customers for excluded autocomplete
  const { data: excludedCustomersData, isLoading: excludedCustomersLoading } =
    useCustomers({
      pageSize: 10,
      name:
        excludedCustomerSearchQuery.length > 2
          ? excludedCustomerSearchQuery
          : undefined,
    });

  // Fetch selected customers by IDs to show their names
  const { data: selectedCustomerDetails } = useCustomersByIds(
    filters.customerIds,
    !!filters.customerIds?.length,
  );

  // Fetch excluded customers by IDs to show their names
  const { data: excludedCustomerDetails } = useCustomersByIds(
    filters.excludedCustomerIds,
    !!filters.excludedCustomerIds?.length,
  );

  // Derive selected customers from fetched details (no useEffect needed)
  const selectedCustomers = React.useMemo(
    () => selectedCustomerDetails || [],
    [selectedCustomerDetails],
  );
  const excludedCustomers = React.useMemo(
    () => excludedCustomerDetails || [],
    [excludedCustomerDetails],
  );

  // Detect orphaned customer IDs (exist in filters but not found in the current environment)
  // This can happen when switching between environments locally
  const orphanedCustomerIds = React.useMemo(() => {
    if (!filters.customerIds?.length) return [];
    const foundIds = new Set(selectedCustomers.map((c) => c.customerId));
    return filters.customerIds.filter((id) => !foundIds.has(id));
  }, [filters.customerIds, selectedCustomers]);

  const orphanedExcludedCustomerIds = React.useMemo(() => {
    if (!filters.excludedCustomerIds?.length) return [];
    const foundIds = new Set(excludedCustomers.map((c) => c.customerId));
    return filters.excludedCustomerIds.filter((id) => !foundIds.has(id));
  }, [filters.excludedCustomerIds, excludedCustomers]);

  // Search results; the selected customers are fetched separately above and
  // passed to the pickers as `selectedItems`.
  const customerOptions = (customersData?.edges || []).map(convertToBasicInfo);
  const excludedCustomerOptions = (excludedCustomersData?.edges || []).map(
    convertToBasicInfo,
  );

  const handleCustomerChange = (customerIds: string[]) => {
    onFiltersChange({
      ...filters,
      customerIds: customerIds.length ? customerIds : undefined,
    });
  };

  const handleExcludedCustomerChange = (customerIds: string[]) => {
    onFiltersChange({
      ...filters,
      excludedCustomerIds: customerIds.length ? customerIds : undefined,
    });
  };

  const handleStatusChange = (statuses: string[]) => {
    onFiltersChange({
      ...filters,
      statuses: statuses.length
        ? (statuses as SalesOrderDraftStatus[])
        : undefined,
    });
  };

  const handleSourceChange = (sources: string[]) => {
    onFiltersChange({
      ...filters,
      sources: sources.length ? (sources as SalesOrderSource[]) : undefined,
    });
  };

  const handleWatchStatusChange = (watchStatuses: string[]) => {
    onFiltersChange({
      ...filters,
      watchStatus: watchStatuses.length
        ? (watchStatuses as WatchStatus[])
        : undefined,
    });
  };

  const handlePONumberChange = (value: string) => {
    onFiltersChange({
      ...filters,
      purchaseOrderNumber: value || undefined,
    });
  };

  const hasActiveFilters =
    (filters.customerIds && filters.customerIds.length) ||
    (filters.excludedCustomerIds && filters.excludedCustomerIds.length) ||
    (filters.statuses && filters.statuses.length) ||
    (filters.sources && filters.sources.length) ||
    (showWatchFilter && filters.watchStatus && filters.watchStatus.length) ||
    !!filters.purchaseOrderNumber ||
    !!filters.salesAssigneeUserId ||
    (showDateFilter && !!(filters.receivedFrom || filters.receivedTo)) ||
    (showErpPendingFilter && !!filters.erpNumberPending);

  const getOrphanCustomerTitle = useCallback(
    (customerId: string) =>
      gt('Customer ID: {customerId} (not found in current environment)', {
        customerId,
        context:
          'tooltip when a saved customer filter id is missing in this environment',
      }),
    [gt],
  );

  return (
    <div className="space-y-2">
      {/* Column count tracks the number of rendered cells so the inputs
          stretch across the full row with no gap at the end. */}
      <div
        className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 ${
          showWatchFilter || showAssigneeFilter
            ? 'xl:grid-cols-6'
            : 'xl:grid-cols-5'
        } gap-2`}
      >
        {/* Filter by PO Number */}
        <FloatingLabelWrapper
          label={
            <T context="sales order list filter label">Filter by PO Number</T>
          }
          hasValue={!!filters.purchaseOrderNumber}
        >
          <Input
            placeholder=""
            value={filters.purchaseOrderNumber || ''}
            onChange={(e) => handlePONumberChange(e.target.value)}
          />
        </FloatingLabelWrapper>

        {/* Filter by Customer */}
        <FloatingLabelWrapper
          label={
            <T context="sales order list filter label">Filter by Customer</T>
          }
          hasValue={!!(filters.customerIds && filters.customerIds.length > 0)}
        >
          <Autocomplete
            multiple
            items={customerOptions}
            selectedItems={selectedCustomers}
            search={customerSearchQuery}
            onSearchChange={setCustomerSearchQuery}
            // The server searches from 3 characters; below that, narrow the first page here.
            filter={customerSearchQuery.length > 2 ? null : undefined}
            pinSelected
            getValue={(option) => option.customerId}
            getLabel={(option) => option.erpDisplayName ?? option.name}
            value={filters.customerIds}
            onValueChange={handleCustomerChange}
            loading={customersLoading}
            labels={pickerLabels({
              placeholder: '',
              search: gt('Start typing to search customers...', {
                context: 'sales order customer filter autocomplete',
              }),
              empty: gt('No customers found', {
                context: 'autocomplete empty state',
              }),
            })}
            className="w-full"
          />
        </FloatingLabelWrapper>

        {/* Exclude Customers */}
        <FloatingLabelWrapper
          label={
            <T context="sales order list filter label">Exclude Customers</T>
          }
          hasValue={
            !!(
              filters.excludedCustomerIds &&
              filters.excludedCustomerIds.length > 0
            )
          }
        >
          <Autocomplete
            multiple
            items={excludedCustomerOptions}
            selectedItems={excludedCustomers}
            search={excludedCustomerSearchQuery}
            onSearchChange={setExcludedCustomerSearchQuery}
            filter={excludedCustomerSearchQuery.length > 2 ? null : undefined}
            pinSelected
            getValue={(option) => option.customerId}
            getLabel={(option) => option.erpDisplayName ?? option.name}
            value={filters.excludedCustomerIds}
            onValueChange={handleExcludedCustomerChange}
            loading={excludedCustomersLoading}
            labels={pickerLabels({
              placeholder: '',
              search: gt('Start typing to search customers...', {
                context: 'sales order excluded customer filter autocomplete',
              }),
              empty: gt('No customers found', {
                context: 'autocomplete empty state',
              }),
            })}
            className="w-full"
          />
        </FloatingLabelWrapper>

        {/* Filter by Status / Stage */}
        <FloatingLabelWrapper
          label={
            statusFilterLabel ? (
              statusFilterLabel
            ) : (
              <T context="sales order list filter label">Filter by Status</T>
            )
          }
          hasValue={!!(filters.statuses && filters.statuses.length > 0)}
        >
          <Autocomplete
            multiple
            items={statusOptions}
            getValue={(option) => option.value}
            getLabel={(option) => option.label}
            value={filters.statuses}
            onValueChange={handleStatusChange}
            labels={pickerLabels({
              placeholder: '',
              search: gt('Search status...', {
                context: 'sales order status filter autocomplete',
              }),
            })}
            className="w-full"
          />
        </FloatingLabelWrapper>

        {/* Filter by Source */}
        <FloatingLabelWrapper
          label={
            <T context="sales order list filter label">Filter by Source</T>
          }
          hasValue={!!(filters.sources && filters.sources.length > 0)}
        >
          <Autocomplete
            multiple
            items={sourceOptions}
            getValue={(option) => option.value}
            getLabel={(option) => option.label}
            value={filters.sources}
            onValueChange={handleSourceChange}
            labels={pickerLabels({
              placeholder: '',
              search: gt('Search source...', {
                context: 'sales order source filter autocomplete',
              }),
            })}
            className="w-full"
          />
        </FloatingLabelWrapper>

        {/* Watching / Assigned */}
        {showAssigneeFilter && (
          <FloatingLabelWrapper
            label={
              <T context="sales order list filter label">Filter by User</T>
            }
            hasValue={!!filters.salesAssigneeUserId}
          >
            <Autocomplete
              deselectable
              allowClear
              items={assigneeOptions}
              getValue={(option) => option.userId}
              getLabel={(option) => option.userLabel ?? option.userId}
              value={filters.salesAssigneeUserId || null}
              onValueChange={(value) =>
                onFiltersChange({
                  ...filters,
                  salesAssigneeUserId: value || undefined,
                })
              }
              labels={pickerLabels({
                placeholder: '',
                search: gt('Search people...', {
                  context: 'sales order user filter autocomplete',
                }),
                empty: gt('No one with a sales assignment found', {
                  context: 'sales order user filter empty state',
                }),
              })}
              className="w-full"
            />
          </FloatingLabelWrapper>
        )}

        {showWatchFilter && (
          <FloatingLabelWrapper
            label={
              <T context="sales order list filter label">Watching / Assigned</T>
            }
            hasValue={!!(filters.watchStatus && filters.watchStatus.length > 0)}
          >
            <Autocomplete
              multiple
              items={watchStatusOptions}
              getValue={(option) => option.value}
              getLabel={(option) => option.label}
              value={filters.watchStatus}
              onValueChange={handleWatchStatusChange}
              labels={pickerLabels({
                placeholder: '',
                search: gt('Search...', {
                  context: 'sales order watch status filter autocomplete',
                }),
              })}
              className="w-full"
            />
          </FloatingLabelWrapper>
        )}
      </div>

      {(showDateFilter || showErpPendingFilter) && (
        <div className="flex flex-wrap items-center gap-3">
          {showDateFilter && (
            <>
              <span className="text-sm text-muted-foreground">
                <T context="sales order list received-date filter label">
                  Received
                </T>
              </span>
              <DateRangePresetFilter
                placeholder={gt('Received date', {
                  context:
                    'sales orders received-date range picker placeholder',
                })}
                value={{ from: filters.receivedFrom, to: filters.receivedTo }}
                onChange={(range) =>
                  onFiltersChange({
                    ...filters,
                    receivedFrom: range.from,
                    receivedTo: range.to,
                  })
                }
              />
            </>
          )}
          {showErpPendingFilter && (
            <Label className="flex cursor-pointer items-center gap-2 text-sm font-normal">
              <Checkbox
                checked={!!filters.erpNumberPending}
                onCheckedChange={(checked) =>
                  onFiltersChange({
                    ...filters,
                    erpNumberPending: checked === true || undefined,
                  })
                }
              />
              <T context="sales order list checkbox: only orders submitted to the ERP whose ERP order number is still pending">
                Pending ERP Order #
              </T>
            </Label>
          )}
        </div>
      )}

      {/* Active filters display */}
      {hasActiveFilters && (
        <div className="flex justify-between items-center">
          <div className="flex gap-2 flex-wrap">
            {filters.salesAssigneeUserId ? (
              <SalesAssigneeBadge
                userId={filters.salesAssigneeUserId}
                onClear={() =>
                  onFiltersChange({
                    ...filters,
                    salesAssigneeUserId: undefined,
                  })
                }
              />
            ) : null}
            {selectedCustomers.map((cust) => (
              <Badge
                key={cust.customerId}
                variant="outline"
                className="cursor-pointer hover:bg-secondary pr-1"
              >
                <T context="active filter badge for included customer">
                  Customer: <Var>{cust.erpDisplayName ?? cust.name}</Var>
                </T>
                <button
                  onClick={() => {
                    const remaining =
                      filters.customerIds?.filter(
                        (id) => id !== cust.customerId,
                      ) || [];
                    handleCustomerChange(remaining);
                  }}
                  className="ml-1 rounded-full hover:bg-secondary/80 p-0.5"
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {/* Orphaned customer IDs - exist in filters but not found in current environment */}
            {orphanedCustomerIds.map((id) => (
              <Badge
                key={id}
                variant="outline"
                className="cursor-pointer hover:bg-secondary pr-1 border-dashed border-amber-400"
                title={getOrphanCustomerTitle(id)}
              >
                <T context="unknown customer placeholder in filter badge">
                  Customer: Unknown
                </T>
                <button
                  onClick={() => {
                    const remaining =
                      filters.customerIds?.filter((cid) => cid !== id) || [];
                    handleCustomerChange(remaining);
                  }}
                  className="ml-1 rounded-full hover:bg-secondary/80 p-0.5"
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {excludedCustomers.map((cust) => (
              <Badge
                key={cust.customerId}
                variant="exclude"
                className="cursor-pointer hover:bg-red-100 pr-1"
              >
                <T context="active filter badge for excluded customer">
                  Exclude: <Var>{cust.erpDisplayName ?? cust.name}</Var>
                </T>
                <button
                  onClick={() => {
                    const remaining =
                      filters.excludedCustomerIds?.filter(
                        (id) => id !== cust.customerId,
                      ) || [];
                    handleExcludedCustomerChange(remaining);
                  }}
                  className="ml-1 rounded-full hover:bg-red-200 p-0.5"
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {/* Orphaned excluded customer IDs - exist in filters but not found in current environment */}
            {orphanedExcludedCustomerIds.map((id) => (
              <Badge
                key={id}
                variant="exclude"
                className="cursor-pointer hover:bg-red-100 pr-1 border-dashed border-amber-400"
                title={getOrphanCustomerTitle(id)}
              >
                <T context="unknown excluded customer in filter badge">
                  Exclude: Unknown
                </T>
                <button
                  onClick={() => {
                    const remaining =
                      filters.excludedCustomerIds?.filter(
                        (cid) => cid !== id,
                      ) || [];
                    handleExcludedCustomerChange(remaining);
                  }}
                  className="ml-1 rounded-full hover:bg-red-200 p-0.5"
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {(filters.statuses || []).map((status) => (
              <Badge
                key={status}
                variant="outline"
                className="cursor-pointer hover:bg-secondary pr-1"
              >
                <T context="active filter badge for draft status">
                  Status:{' '}
                  <Var>
                    {statusOptions.find((s) => s.value === status)?.label}
                  </Var>
                </T>
                <button
                  onClick={() => {
                    const newStatuses = (filters.statuses || []).filter(
                      (s) => s !== status,
                    );
                    handleStatusChange(newStatuses);
                  }}
                  className="ml-1 rounded-full hover:bg-secondary/80 p-0.5"
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {(filters.sources || []).map((src) => (
              <Badge
                key={src}
                variant="outline"
                className="cursor-pointer hover:bg-secondary pr-1"
              >
                <T context="active filter badge for order source">
                  Source:{' '}
                  <Var>{sourceOptions.find((s) => s.value === src)?.label}</Var>
                </T>
                <button
                  onClick={() => {
                    const newSources = (filters.sources || []).filter(
                      (s) => s !== src,
                    );
                    handleSourceChange(newSources);
                  }}
                  className="ml-1 rounded-full hover:bg-secondary/80 p-0.5"
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {(showWatchFilter ? filters.watchStatus || [] : []).map(
              (watchStatus) => (
                <Badge
                  key={watchStatus}
                  variant="outline"
                  className="cursor-pointer hover:bg-secondary pr-1"
                >
                  <T context="active filter badge for watch assignment">
                    Watch:{' '}
                    <Var>
                      {
                        watchStatusOptions.find((w) => w.value === watchStatus)
                          ?.label
                      }
                    </Var>
                  </T>
                  <button
                    onClick={() => {
                      const newWatchStatuses = (
                        filters.watchStatus || []
                      ).filter((w) => w !== watchStatus);
                      handleWatchStatusChange(newWatchStatuses);
                    }}
                    className="ml-1 rounded-full hover:bg-secondary/80 p-0.5"
                  >
                    <XIcon className="h-3 w-3" />
                  </button>
                </Badge>
              ),
            )}
            {filters.purchaseOrderNumber && (
              <Badge
                variant="outline"
                className="cursor-pointer hover:bg-secondary pr-1"
              >
                <T context="active filter badge for purchase order number">
                  PO: <Var>{filters.purchaseOrderNumber}</Var>
                </T>
                <button
                  onClick={() => handlePONumberChange('')}
                  className="ml-1 rounded-full hover:bg-secondary/80 p-0.5"
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {showDateFilter && (filters.receivedFrom || filters.receivedTo) && (
              <Badge
                variant="outline"
                className="cursor-pointer hover:bg-secondary pr-1"
              >
                <T context="active filter badge for received-date range">
                  Received:{' '}
                  <Var>
                    {filters.receivedFrom
                      ? formatDateOnlyString(filters.receivedFrom)
                      : '…'}
                  </Var>{' '}
                  –{' '}
                  <Var>
                    {filters.receivedTo
                      ? formatDateOnlyString(filters.receivedTo)
                      : '…'}
                  </Var>
                </T>
                <button
                  type="button"
                  aria-label={gt('Clear received date filter', {
                    context:
                      'accessible label for the x that clears the received-date filter badge',
                  })}
                  onClick={() =>
                    onFiltersChange({
                      ...filters,
                      receivedFrom: undefined,
                      receivedTo: undefined,
                    })
                  }
                  className="ml-1 rounded-full hover:bg-secondary/80 p-0.5"
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {showErpPendingFilter && filters.erpNumberPending && (
              <Badge
                variant="outline"
                className="cursor-pointer hover:bg-secondary pr-1"
              >
                <T context="active filter badge: ERP order number still pending">
                  ERP Order #: Pending
                </T>
                <button
                  type="button"
                  aria-label={gt('Clear pending ERP order number filter', {
                    context:
                      'accessible label for the x that clears the pending-ERP-order-number filter badge',
                  })}
                  onClick={() =>
                    onFiltersChange({ ...filters, erpNumberPending: undefined })
                  }
                  className="ml-1 rounded-full hover:bg-secondary/80 p-0.5"
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </Badge>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesOrderFilters;
